import React, { useState, useMemo, useRef, useEffect, useCallback } from "react";
import "./FilterableTable.css";

const ROWS_PER_PAGE = 15;

/* ─── tiny dropdown arrow icon ─── */
const FilterIcon = ({ active }) => (
  <svg
    width="10"
    height="10"
    viewBox="0 0 10 10"
    className={`ft-arrow ${active ? "ft-arrow-active" : ""}`}
  >
    <path d="M1 3 L5 7 L9 3" fill="none" stroke="currentColor" strokeWidth="1.6" />
  </svg>
);

/* ─── Excel-style filter popup ─── */
function FilterPopup({ col, allValues, selected, onApply, onClear, onClose }) {
  const [search, setSearch] = useState("");
  const [checked, setChecked] = useState(() => new Set(selected || allValues));
  const popupRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (popupRef.current && !popupRef.current.contains(e.target)) onClose();
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [onClose]);

  const visible = useMemo(() => {
    if (!search) return allValues;
    const q = search.toLowerCase();
    return allValues.filter((v) => String(v).toLowerCase().includes(q));
  }, [allValues, search]);

  const allVisible = visible.length > 0 && visible.every((v) => checked.has(v));

  const toggleAll = () => {
    setChecked((prev) => {
      const next = new Set(prev);
      if (allVisible) {
        visible.forEach((v) => next.delete(v));
      } else {
        visible.forEach((v) => next.add(v));
      }
      return next;
    });
  };

  const toggle = (val) => {
    setChecked((prev) => {
      const next = new Set(prev);
      next.has(val) ? next.delete(val) : next.add(val);
      return next;
    });
  };

  const fmt = (v) => {
    if (v == null) return "(Blank)";
    if (typeof v === "number") return Number.isInteger(v) ? String(v) : v.toFixed(3);
    return String(v);
  };

  return (
    <div className="ft-popup" ref={popupRef}>
      <input
        type="text"
        className="ft-popup-search"
        placeholder="Search..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        autoFocus
      />
      <div className="ft-popup-list">
        <label className="ft-popup-item ft-popup-select-all">
          <input type="checkbox" checked={allVisible} onChange={toggleAll} />
          <span>(Select All)</span>
        </label>
        {visible.map((v, i) => (
          <label key={i} className="ft-popup-item">
            <input
              type="checkbox"
              checked={checked.has(v)}
              onChange={() => toggle(v)}
            />
            <span>{fmt(v)}</span>
          </label>
        ))}
        {visible.length === 0 && (
          <div className="ft-popup-empty">No matches</div>
        )}
      </div>
      <div className="ft-popup-actions">
        <button
          className="ft-popup-btn ft-popup-ok"
          onClick={() => {
            onApply(col, checked.size === allValues.length ? null : checked);
            onClose();
          }}
        >
          OK
        </button>
        <button
          className="ft-popup-btn ft-popup-clear"
          onClick={() => {
            onClear(col);
            onClose();
          }}
        >
          Clear
        </button>
      </div>
    </div>
  );
}

/* ─── Main table component ─── */
function FilterableTable({ columns, data }) {
  const [filters, setFilters] = useState({});
  const [openFilter, setOpenFilter] = useState(null);
  const [expanded, setExpanded] = useState(false);

  const uniqueValues = useMemo(() => {
    if (!data || !columns) return {};
    const result = {};
    columns.forEach((col) => {
      const vals = [...new Set(data.map((r) => r[col]))].filter((v) => v != null);
      vals.sort((a, b) => {
        if (typeof a === "number" && typeof b === "number") return a - b;
        return String(a).localeCompare(String(b));
      });
      result[col] = vals;
    });
    return result;
  }, [columns, data]);

  const filteredData = useMemo(() => {
    if (!data) return [];
    return data.filter((row) =>
      Object.entries(filters).every(([col, allowedSet]) => {
        if (!allowedSet) return true;
        const cell = row[col];
        return allowedSet.has(cell);
      })
    );
  }, [data, filters]);

  const displayData = expanded ? filteredData : filteredData.slice(0, ROWS_PER_PAGE);

  const applyFilter = useCallback((col, checkedSet) => {
    setFilters((prev) => {
      const next = { ...prev };
      if (!checkedSet) {
        delete next[col];
      } else {
        next[col] = checkedSet;
      }
      return next;
    });
  }, []);

  const clearFilter = useCallback((col) => {
    setFilters((prev) => {
      const next = { ...prev };
      delete next[col];
      return next;
    });
  }, []);

  const handleToggleFilter = (col) => {
    setOpenFilter((prev) => (prev === col ? null : col));
  };

  const fmtCell = (v) => {
    if (v == null) return "—";
    if (typeof v === "number") return Number.isInteger(v) ? v : v.toFixed(3);
    return v;
  };

  if (!columns || !data) return null;

  return (
    <div className="ft-container">
      <div className="ft-table-wrap">
        <table className="ft-table">
          <thead>
            <tr>
              {columns.map((col) => {
                const isActive = !!filters[col];
                return (
                  <th key={col}>
                    <div className="ft-th-inner">
                      <span className="ft-th-label">{col}</span>
                      <button
                        className={`ft-filter-btn ${isActive ? "ft-filter-active" : ""}`}
                        onClick={() => handleToggleFilter(col)}
                        title={`Filter ${col}`}
                      >
                        <FilterIcon active={isActive} />
                      </button>
                    </div>
                    {openFilter === col && (
                      <FilterPopup
                        col={col}
                        allValues={uniqueValues[col] || []}
                        selected={filters[col]}
                        onApply={applyFilter}
                        onClear={clearFilter}
                        onClose={() => setOpenFilter(null)}
                      />
                    )}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {displayData.map((row, idx) => (
              <tr key={idx}>
                {columns.map((col) => {
                  const val = row[col];
                  const isActual = col === "Actual Permeability";
                  const isPred = col === "Predicted Permeability";
                  return (
                    <td
                      key={col}
                      className={isActual ? "cell-actual" : isPred ? "cell-predicted" : ""}
                    >
                      {fmtCell(val)}
                    </td>
                  );
                })}
              </tr>
            ))}
            {displayData.length === 0 && (
              <tr>
                <td colSpan={columns.length} className="ft-empty">
                  No matching data found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="ft-footer">
        <span>
          Showing {displayData.length} of {filteredData.length} rows
        </span>
        {filteredData.length > ROWS_PER_PAGE && (
          <button className="ft-expand-btn" onClick={() => setExpanded(!expanded)}>
            {expanded ? "Collapse Table" : "Expand Table"}
          </button>
        )}
      </div>
    </div>
  );
}

export default FilterableTable;
